import React, { useState, useEffect } from 'react'
import {
  Card, Row, Col, DatePicker, Button, Space, Table, Statistic, Select, Tag, App
} from 'antd'
import { DownloadOutlined, ReloadOutlined, DollarOutlined } from '@ant-design/icons'
import { billingApi, analyticsApi } from '@/services/api'
import ReactECharts from 'echarts-for-react'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select

interface BillingRecord {
  id: number
  model: string
  model_name: string
  provider: string
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  input_cost: number
  output_cost: number
  total_cost: number
  currency: string
  created_at: number
}

interface BillingStats {
  total_records: number
  total_tokens: number
  total_cost: number
  avg_cost_per_message: number
  min_cost: number
  max_cost: number
}

const TokenBillingPage: React.FC = () => {
  const { message } = App.useApp()
  const [records, setRecords] = useState<BillingRecord[]>([])
  const [stats, setStats] = useState<BillingStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })

  const [trendChartData, setTrendChartData] = useState<any>(null)
  const [costChartData, setCostChartData] = useState<any>(null)

  const fetchData = async (page = 1) => {
    try {
      setLoading(true)
      const params = {
        page,
        limit: pagination.pageSize,
        model: selectedModel || undefined,
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf()
      }

      const [recordsResponse, statsResponse] = await Promise.all([
        billingApi.getBillingRecords(params),
        billingApi.getBillingStats(params)
      ])

      if (recordsResponse.success) {
        console.log('🔍 Records response:', recordsResponse); // 🔥 调试日志
        setRecords(recordsResponse.data || [])
        setPagination(prev => ({
          ...prev,
          current: page,
          total: recordsResponse.pagination?.total || 0
        }))
      }

      if (statsResponse.success) {
        console.log('🔍 Stats response:', statsResponse); // 🔥 调试日志
        console.log('🔍 Total stats:', statsResponse.data.totalStats); // 🔥 调试日志
        setStats(statsResponse.data.totalStats)
        await updateCharts(statsResponse.data)
      }
    } catch (error) {
      console.error('获取计费数据失败:', error)
      message.error('获取计费数据失败')
    } finally {
      setLoading(false)
    }
  }

  const updateCharts = async (billingData: any) => {
    // 获取趋势数据
    try {
      const trendsResponse = await analyticsApi.getTokenUsageTrends({
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf(),
        model: selectedModel || undefined,
        granularity: 'day'
      })

      if (trendsResponse.success) {
        const trendOption = {
          title: {
            text: 'Token使用趋势',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['消息数', 'Token数'],
            top: 30
          },
          xAxis: {
            type: 'category',
            data: trendsResponse.data.trends.map((item: any) =>
              dayjs(item.time_period).format('MM-DD')
            )
          },
          yAxis: [
            {
              type: 'value',
              name: '消息数',
              position: 'left'
            },
            {
              type: 'value',
              name: 'Token数',
              position: 'right'
            }
          ],
          series: [
            {
              name: '消息数',
              type: 'line',
              data: trendsResponse.data.trends.map((item: any) => item.message_count),
              itemStyle: { color: '#1890ff' }
            },
            {
              name: 'Token数',
              type: 'line',
              yAxisIndex: 1,
              data: trendsResponse.data.trends.map((item: any) => item.total_tokens),
              itemStyle: { color: '#52c41a' }
            }
          ]
        }
        setTrendChartData(trendOption)
      }
    } catch (error) {
      console.error('获取趋势数据失败:', error)
    }

    // 更新成本分布图
    if (billingData.modelStats) {
      const costOption = {
        title: {
          text: '模型成本分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: ${c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: '成本',
            type: 'pie',
            radius: '50%',
            data: billingData.modelStats.map((item: any) => ({
              value: item.total_cost,
              name: item.model_name || item.model
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }
      setCostChartData(costOption)
    }
  }



  const handleExport = async () => {
    try {
      const params = {
        model: selectedModel || undefined,
        startDate: dateRange?.[0]?.valueOf(),
        endDate: dateRange?.[1]?.valueOf()
      }

      await billingApi.exportBillingRecords(params)
      message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败')
    }
  }

  // 🔥 新增：批量生成缺失的计费记录
  const handleGenerateMissingRecords = async () => {
    try {
      setLoading(true)
      const response = await billingApi.generateMissingBillingRecords()

      if (response.success) {
        message.success(`成功生成 ${response.data.generated} 条计费记录`)
        // 重新加载数据
        await fetchData()
      } else {
        message.error('生成计费记录失败: ' + response.error)
      }
    } catch (error: any) {
      console.error('生成计费记录失败:', error)
      message.error('生成计费记录失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 🔥 新增：重新计算现有记录的费用
  const handleRecalculateCosts = async () => {
    try {
      setLoading(true)
      const response = await billingApi.recalculateBillingRecordsCosts()

      if (response.success) {
        message.success(`成功重新计算 ${response.data.updated} 条记录的费用`)
        // 重新加载数据
        await fetchData()
      } else {
        message.error('重新计算费用失败: ' + response.error)
      }
    } catch (error: any) {
      console.error('重新计算费用失败:', error)
      message.error('重新计算费用失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    fetchData()
  }, [dateRange, selectedModel])

  // 🔥 修复：增强 formatCost 函数的类型检查，防止 toFixed 错误
  const formatCost = (cost: any) => {
    console.log('formatCost input:', cost, 'type:', typeof cost); // 🔥 调试日志

    // 🔥 修复：更严格的类型检查
    if (cost === null || cost === undefined || cost === '') {
      console.log('formatCost: null/undefined/empty, returning 0.0000');
      return '0.0000'
    }

    // 🔥 修复：尝试转换为数字
    const numCost = Number(cost)
    console.log('formatCost numCost:', numCost, 'isNaN:', isNaN(numCost), 'isFinite:', isFinite(numCost));

    if (isNaN(numCost) || !isFinite(numCost)) {
      console.log('formatCost: NaN or not finite, returning 0.0000');
      return '0.0000'
    }

    // 🔥 修复：确保是正数
    const safeCost = Math.max(0, numCost)
    const result = safeCost < 0.01 ? safeCost.toFixed(6) : safeCost.toFixed(4)
    console.log('formatCost result:', result);
    return result
  }

  const columns = [
    {
      title: '模型信息',
      key: 'model_info',
      width: 280,
      render: (_: any, record: BillingRecord) => (
        <div>
          {/* 🔥 修复：加粗显示model_id，调换显示顺序 */}
          <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: '14px' }}>
            {record.model}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            {record.model_name || record.model}
          </div>
          {/* 🔥 新增：显示item_id（如果存在） */}
          {(record as any).item_id && (
            <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace', marginBottom: 4 }}>
              ID: {(record as any).item_id}
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            <Tag key="provider" color="blue">{record.provider || '未知'}</Tag>
            {(record as any).model_type && (
              <Tag key="model_type" color="green">{(record as any).model_type}</Tag>
            )}
            {(record as any).tier && (
              <Tag key="tier" color="purple">{(record as any).tier}</Tag>
            )}
          </div>
          {/* 🔥 新增：显示模型能力 */}
          <div style={{ marginTop: 4 }}>
            {(record as any).supports_vision && <Tag key="vision" color="orange">视觉</Tag>}
            {(record as any).supports_reasoning && <Tag key="reasoning" color="red">推理</Tag>}
            {(record as any).supports_code && <Tag key="code" color="geekblue">代码</Tag>}
            {(record as any).supports_function_calling && <Tag key="function" color="cyan">函数</Tag>}
          </div>
        </div>
      )
    },
    {
      title: '输入Token',
      dataIndex: 'prompt_tokens',
      key: 'prompt_tokens',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString()
    },
    {
      title: '输出Token',
      dataIndex: 'completion_tokens',
      key: 'completion_tokens',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString()
    },
    {
      title: '总Token',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString()
    },
    {
      title: '输入费用',
      dataIndex: 'input_cost',
      key: 'input_cost',
      width: 100,
      align: 'right' as const,
      render: (value: number) => `$${formatCost(value)}`
    },
    {
      title: '输出费用',
      dataIndex: 'output_cost',
      key: 'output_cost',
      width: 100,
      align: 'right' as const,
      render: (value: number) => `$${formatCost(value)}`
    },
    {
      title: '总费用',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 100,
      align: 'right' as const,
      render: (value: number) => (
        <Tag icon={<DollarOutlined />} color="green">
          ${formatCost(value)}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (timestamp: number) => dayjs(timestamp).format('YYYY-MM-DD HH:mm')
    }
  ]

  return (
    <div style={{ padding: '0' }}>
      <Card title="Token计费分析" extra={
        <Space>
          <Select
            placeholder="选择模型"
            value={selectedModel || undefined}
            onChange={setSelectedModel}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="anthropic/claude-3.7-sonnet">Claude 3.7 Sonnet</Option>
            <Option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</Option>
            <Option value="anthropic/claude-sonnet-4">Claude Sonnet 4</Option>
            <Option value="openai/gpt-4.1">GPT-4.1</Option>
            <Option value="openai/gpt-4o">GPT-4o</Option>
            <Option value="deepseek/deepseek-r1">DeepSeek R1</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
            刷新
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
            导出
          </Button>
          <Button
            type="default"
            loading={loading}
            onClick={handleGenerateMissingRecords}
          >
            🔄 生成缺失计费记录
          </Button>
          <Button
            type="default"
            loading={loading}
            onClick={handleRecalculateCosts}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
          >
            💰 重新计算费用
          </Button>
        </Space>
      }>
        {/* 统计概览 */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Statistic
                title="总记录数"
                value={stats.total_records}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="总Token数"
                value={stats.total_tokens}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="总费用"
                value={`$${formatCost(stats.total_cost)}`}
                valueStyle={{ color: '#722ed1' }}
                prefix={<DollarOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="平均费用"
                value={`$${formatCost(stats.avg_cost_per_message)}`}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Col>
          </Row>
        )}

        {/* 图表展示 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card title="使用趋势" size="small">
              {trendChartData ? (
                <ReactECharts
                  option={trendChartData}
                  style={{ width: '100%', height: '300px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="成本分布" size="small">
              {costChartData ? (
                <ReactECharts
                  option={costChartData}
                  style={{ width: '100%', height: '300px' }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              ) : (
                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  暂无数据
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 详细记录表格 */}
        <Table
          columns={columns}
          dataSource={records}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(paginationInfo) => {
            fetchData(paginationInfo.current)
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  )
}

export default TokenBillingPage
